var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { Box, Button } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import React from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { FormViewer } from '../FormViewer';
import { callConfigFunction } from '../WidgetForm/Utils';
/**
 * Component that renders a form and handles data submission.
 *
 * Important! Props that control how form is rendered do not react to changes. Changing these props will not re-render
 * the form. If you need to re-rerender the form when form configuration changes, use `FormViewer` component or control
 * re-render manually by changing the standard React `key` prop. This behavior is intentional.
 *
 * List of props that do not react to changed:
 * - `config`
 * - `data`
 * - `formParams`
 * - `valueProviders`
 * - `subFormProviders`
 * - `rules`
 * - `widgets`
 * - `globalWidgetConfig`
 * - `showErrors`.
 *
 * @see defineFormComponent
 */
export const Form = React.forwardRef(FormRenderFunction);
/**
 * Helper function to construct typesafe Form component.
 * Can be used to achieve proper typing for data that form collects.
 * Also allows to pass custom widget types in form config.
 *
 * @see Form
 *
 * @returns {Form} Form.
 */
export function defineFormComponent() {
    return Form;
}
function FormRenderFunction({ config, data, formParams, submitLabel, cancelLabel, valueProviders, subFormProviders, rules, widgets, globalWidgetConfig, hideFormControls, showErrors: initialShowErrors, trackChanges, trackChangesMessage, cancelNavigationOnAutoSaveError, autoSaveDebounceTimeout, onCancel, onChanged, onSubmit, onSubmitSuccess, onSubmitError, changeUndefinedTopFieldsToNull, 'data-test': dataTest, readOnly, }, ref) {
    const navigationContext = React.useContext(NavigationContext);
    const initialConfig = React.useRef(config);
    const initialDataSet = React.useRef(false);
    const initialData = React.useRef(undefined);
    if (!initialDataSet.current) {
        initialDataSet.current = true;
        initialData.current = 'onLoad' in config && config.onLoad ? callConfigFunction(config.onLoad, 'form onLoad', 'data', data) : data;
    }
    const initialWidgets = React.useRef(widgets);
    const initialGlobalWidgetConfig = React.useRef(globalWidgetConfig);
    const initialFormParams = React.useRef(formParams);
    const initialValueProviders = React.useRef(valueProviders);
    const initialSubFormProviders = React.useRef(subFormProviders);
    const initialRules = React.useRef(rules);
    const formBoxRef = React.useRef(null);
    const [showErrors, setShowErrors] = React.useState(initialShowErrors !== null && initialShowErrors !== void 0 ? initialShowErrors : 'changed');
    const [widgetData, setWidgetData] = React.useState();
    const [submitInProgress, setSubmitInProgress] = React.useState(false);
    const lastAutoSaveData = React.useRef(undefined);
    const autoSaveTimeoutHandle = React.useRef(undefined);
    const handleCancel = () => __awaiter(this, void 0, void 0, function* () {
        if (onCancel) {
            try {
                setSubmitInProgress(true);
                yield onCancel(widgetData === null || widgetData === void 0 ? void 0 : widgetData.data);
            }
            catch (error) {
                handleErrorDialog(error);
            }
            finally {
                setSubmitInProgress(false);
            }
        }
    });
    const handleSubmit = React.useCallback((widgetData, autoSaveData) => {
        if (onSubmit) {
            if (widgetData === null || widgetData === void 0 ? void 0 : widgetData.valid) {
                setSubmitInProgress(true);
                if ((widgetData === null || widgetData === void 0 ? void 0 : widgetData.data) && initialData.current && changeUndefinedTopFieldsToNull) {
                    Object.keys(initialData.current).forEach((key) => {
                        if (widgetData.data[key] === undefined) {
                            widgetData.data[key] = null;
                        }
                    });
                }
                if (autoSaveData != null) {
                    lastAutoSaveData.current = autoSaveData;
                }
                else if (trackChanges === 'auto-save') {
                    lastAutoSaveData.current = cloneDeep(widgetData.data);
                }
                const submitData = 'onSubmit' in initialConfig.current && initialConfig.current.onSubmit
                    ? callConfigFunction(initialConfig.current.onSubmit, 'form onSubmit', 'data', widgetData.data)
                    : widgetData.data;
                return onSubmit(submitData)
                    .then((response) => {
                    setSubmitInProgress(false);
                    if (onSubmitSuccess) {
                        onSubmitSuccess(submitData, response);
                    }
                })
                    .catch((error) => {
                    setSubmitInProgress(false);
                    if (onSubmitError) {
                        onSubmitError(submitData, error);
                    }
                    else if (trackChanges === 'auto-save' && !cancelNavigationOnAutoSaveError) {
                        handleErrorSnackbar(error);
                    }
                    else {
                        handleErrorDialog(error);
                    }
                    throw error;
                });
            }
            else {
                setShowErrors('all');
                // After form has rendered the error we want to scroll to first visible error.
                if (trackChanges !== 'auto-save') {
                    window.setTimeout(() => {
                        if (formBoxRef.current) {
                            const firstError = formBoxRef.current.querySelector('[data-visible-error=true]');
                            if (firstError) {
                                scrollIntoView(firstError, { scrollMode: 'if-needed', behavior: 'smooth' });
                            }
                        }
                    });
                }
                return Promise.reject(new Error('Cannot submit form, because it has errors'));
            }
        }
        else {
            return Promise.reject(new Error('Cannot submit form, because onSubmit prop is not defined'));
        }
    }, [trackChanges, cancelNavigationOnAutoSaveError, onSubmit, onSubmitSuccess, onSubmitError, changeUndefinedTopFieldsToNull]);
    const handleOnChange = React.useCallback((data) => {
        if (onChanged || trackChanges) {
            const submit = () => handleSubmit(data);
            if (onChanged) {
                onChanged(true, submit);
            }
            if (trackChanges === 'auto-save') {
                navigationContext.setAutoSave(submit, cancelNavigationOnAutoSaveError ? { cancelNavigationOnError: true } : undefined);
            }
            else if (trackChanges === 'ask-to-save') {
                navigationContext.setChanged(trackChangesMessage !== null && trackChangesMessage !== void 0 ? trackChangesMessage : 'Save changes?', submit);
            }
            else if (trackChanges === 'confirm-lose-changes') {
                navigationContext.setChanged(trackChangesMessage !== null && trackChangesMessage !== void 0 ? trackChangesMessage : 'Unsaved changes will be lost.');
            }
        }
    }, [trackChanges, trackChangesMessage, cancelNavigationOnAutoSaveError, onChanged, navigationContext, handleSubmit]);
    const handleOnAutoSave = React.useCallback((data, immediately) => {
        if (trackChanges === 'auto-save') {
            if (autoSaveTimeoutHandle.current != null) {
                window.clearTimeout(autoSaveTimeoutHandle.current);
                autoSaveTimeoutHandle.current = undefined;
            }
            const dataClone = cloneDeep(data);
            if (immediately) {
                if (!isEqual(dataClone.data, lastAutoSaveData.current)) {
                    navigationContext.save(handleSubmit(dataClone, dataClone.data));
                }
            }
            else {
                autoSaveTimeoutHandle.current = window.setTimeout(() => {
                    if (!isEqual(dataClone.data, lastAutoSaveData.current)) {
                        navigationContext.save(handleSubmit(dataClone, dataClone.data));
                    }
                }, autoSaveDebounceTimeout !== null && autoSaveDebounceTimeout !== void 0 ? autoSaveDebounceTimeout : 3000);
            }
        }
    }, [trackChanges, autoSaveDebounceTimeout, navigationContext, handleSubmit]);
    React.useEffect(() => {
        if (trackChanges != null) {
            return () => {
                if (autoSaveTimeoutHandle.current) {
                    window.clearTimeout(autoSaveTimeoutHandle.current);
                    autoSaveTimeoutHandle.current = undefined;
                }
                navigationContext.cleanUp();
            };
        }
        else {
            return undefined;
        }
    }, [trackChanges, navigationContext]);
    React.useImperativeHandle(ref, () => {
        return {
            getData: () => widgetData === null || widgetData === void 0 ? void 0 : widgetData.data,
            submit: () => {
                let submitPromise;
                if (onSubmit) {
                    submitPromise = handleSubmit(widgetData);
                }
                else {
                    submitPromise = Promise.reject(new Error('Cannot submit form, onSubmit prop is not defined'));
                }
                if (trackChanges != null) {
                    navigationContext.save(submitPromise);
                }
                return submitPromise;
            },
        };
    });
    return (React.createElement(Box, { ref: formBoxRef, "data-test": dataTest },
        React.createElement(FormViewer, { config: initialConfig.current, data: initialData.current, widgets: initialWidgets.current, showErrors: showErrors, valueProviders: initialValueProviders.current, subFormProviders: initialSubFormProviders.current, rules: initialRules.current, globalWidgetConfig: initialGlobalWidgetConfig.current, formParams: initialFormParams.current, onChange: handleOnChange, onAutoSave: handleOnAutoSave, onDataChange: setWidgetData, readOnly: readOnly }),
        !hideFormControls && trackChanges !== 'auto-save' && (onCancel || onSubmit) && (React.createElement(Box, { sx: { display: 'flex', mt: 2, gap: 2, alignItems: 'center', justifyContent: 'end' } },
            onCancel && (React.createElement(Button, { variant: 'outlined', color: 'primary', disabled: submitInProgress, onClick: handleCancel, "data-test": 'cancel-button' }, cancelLabel !== null && cancelLabel !== void 0 ? cancelLabel : 'Cancel')),
            onSubmit && !readOnly && (React.createElement(Button, { variant: 'contained', color: 'primary', disabled: submitInProgress, onClick: () => {
                    if (trackChanges != null) {
                        navigationContext.save(handleSubmit(widgetData));
                    }
                    else {
                        handleSubmit(widgetData).catch(() => {
                            // errors are handled in handleSubmit function
                        });
                    }
                }, "data-test": 'submit-button' }, submitLabel !== null && submitLabel !== void 0 ? submitLabel : 'Submit'))))));
}
